import React from 'react';
import { useTheme } from '../ThemeContext';
import { toggleFeature } from '../shared/homeownerIntake';
import {
  FILTER_LABELS,
  FILTER_TYPES,
  SEWER_PUBLIC,
  SEWER_SEPTIC,
  VISIBLE_FEATURES,
  HOUSE_WATER_SOURCES,
  IRRIGATION_SOURCES,
  houseWaterIds,
  irrigationSources,
  irrigationUsesWell,
  toggleHouseWater,
  toggleIrrigation,
  featureSelectedVisible,
  hasSeptic,
  hasWell,
  readFilterSizes,
  singleChoice,
} from '../shared/homeFacts';
import {
  HEATER_SYSTEMS,
  HVAC_SYSTEMS,
  ductedHvacCount,
  hvacUnitTotal,
  heaterUnitSlots,
  hvacTypesField,
  hvacUnitSlots,
  oldestHeaterYear,
  parseHvacSystems,
  readHvacUnitYears,
} from '../shared/homeProfile';

function Tile({ id, label, selected, onClick, brandColor }) {
  const { colors, isDark } = useTheme();
  return (
    <button
      type="button"
      data-testid={`home-fact-${id}`}
      aria-pressed={selected}
      onClick={onClick}
      style={{
        minHeight: 48,
        padding: '0.7rem 0.8rem',
        borderRadius: '0.85rem',
        border: selected ? `2px solid ${brandColor}` : `1px solid ${isDark ? '#4a4a4a' : '#d5e4e4'}`,
        background: selected ? (isDark ? '#2c4544' : '#e7f7f7') : colors.surface,
        color: colors.text,
        fontWeight: 650,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function yearInput(value) {
  return value == null || value === '' ? '' : String(value);
}

function CountCard({ id, label, description, count, readOnly, brandColor, onDelta }) {
  const selected = count > 0;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <button
        type="button"
        data-testid={`home-system-${id}`}
        aria-pressed={selected}
        disabled={readOnly}
        onClick={() => onDelta(selected ? -count : 1)}
        style={{
          flex: '1 1 auto',
          minHeight: 48,
          textAlign: 'left',
          borderRadius: '0.85rem',
          border: selected ? `2px solid ${brandColor}` : '1px solid #d5e4e4',
          background: selected ? '#e7f7f7' : '#fff',
          color: '#083838',
          padding: '0.6rem 0.8rem',
          cursor: readOnly ? 'default' : 'pointer',
        }}
      >
        <span style={{ display: 'block', fontWeight: 700, color: '#083838' }}>{label}</span>
        <span style={{ display: 'block', color: '#3f6f6e', fontSize: '0.95rem' }}>{description}</span>
      </button>
      <button type="button" aria-label={`Fewer ${label}`} disabled={readOnly || !count} onClick={() => onDelta(-1)} style={{ minWidth: 44, minHeight: 44 }}>−</button>
      <span aria-label={`${label} count`} style={{ minWidth: 16, textAlign: 'center' }}>{count || 0}</span>
      <button type="button" aria-label={`More ${label}`} disabled={readOnly} onClick={() => onDelta(1)} style={{ minWidth: 44, minHeight: 44 }}>+</button>
    </div>
  );
}

function yearFieldStyle(colors) {
  return {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: 6,
    minHeight: 48,
    borderRadius: '0.75rem',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    padding: '0.6rem 0.75rem',
    fontWeight: 500,
  };
}

export default function HomeFactsFields({ value, onChange, brandColor = '#59c4c4', readOnly = false, equipment = null }) {
  const { colors } = useTheme();
  const home = value || {};
  const listed = Array.isArray(home.hvac_types) && home.hvac_types.length > 0;
  const hvacSystems = parseHvacSystems(listed ? home.hvac_types : null, listed ? null : home.hvac_count);
  const hvacSlots = hvacUnitSlots({ hvac_types: hvacTypesField(hvacSystems), hvac_count: null });
  const alignedHvacYears = readHvacUnitYears({
    hvac_types: hvacTypesField(hvacSystems),
    hvac_count: null,
    hvac_install_years: home.hvac_install_years,
  });
  const hvacYears = Array.isArray(home.hvac_install_years) && home.hvac_install_years.length === hvacSlots.length
    ? home.hvac_install_years.map((item) => (item == null ? '' : String(item)))
    : alignedHvacYears.map((item) => (item ? String(item) : ''));
  const ducted = ductedHvacCount({ hvac_types: hvacTypesField(hvacSystems) });
  const heaterSlots = heaterUnitSlots(home.water_heater_type, home.water_heater_years ? null : home.water_heater_install_year);
  const heaterYears = Array.isArray(home.water_heater_years) && home.water_heater_years.length === heaterSlots.length
    ? home.water_heater_years
    : heaterSlots.map((slot) => (slot.year ? String(slot.year) : ''));
  const sizes = readFilterSizes(home);
  const houseWater = houseWaterIds(home);
  const sewer = hasSeptic(home)
    ? SEWER_SEPTIC
    : singleChoice(home.sewer_type, SEWER_PUBLIC, SEWER_SEPTIC);

  const patch = (next) => {
    if (readOnly) return;
    onChange(Object.assign({}, home, next));
  };

  const setSize = (index, text) => {
    const next = sizes.slice();
    while (next.length < ducted) next.push('');
    next[index] = text;
    patch({ hvac_filter_sizes: next.slice(0, ducted) });
  };

  const setHvacCount = (id, delta) => {
    const next = Object.assign({}, hvacSystems);
    const count = Math.max(0, Math.min(8, (next[id] || 0) + delta));
    if (count === 0) delete next[id];
    else next[id] = count;
    const byType = {};
    hvacSlots.forEach((slot, index) => {
      if (!byType[slot.id]) byType[slot.id] = [];
      byType[slot.id].push(hvacYears[index] || '');
    });
    if (!byType[id]) byType[id] = [];
    while (byType[id].length < (next[id] || 0)) byType[id].push('');
    byType[id] = byType[id].slice(0, next[id] || 0);
    const field = hvacTypesField(next);
    const years = [];
    HVAC_SYSTEMS.forEach((row) => {
      const list = byType[row.id] || [];
      for (let i = 0; i < (next[row.id] || 0); i += 1) years.push(list[i] || null);
    });
    patch({
      hvac_types: field,
      hvac_count: hvacUnitTotal({ hvac_types: field }) || null,
      hvac_install_years: years,
    });
  };

  const setHvacYear = (index, text) => {
    const years = hvacYears.slice();
    years[index] = text.replace(/[^\d]/g, '').slice(0, 4);
    patch({ hvac_install_years: years });
  };

  const writeHeaters = (grouped) => {
    const parts = [];
    const drafts = [];
    HEATER_SYSTEMS.forEach((row) => {
      (grouped[row.id] || []).forEach((year) => {
        const clean = String(year || '').replace(/[^\d]/g, '').slice(0, 4);
        parts.push(clean.length === 4 ? row.id + '@' + clean : row.id);
        drafts.push(clean);
      });
    });
    const encoded = parts.join('|');
    patch({
      water_heater_type: encoded,
      water_heater_install_year: oldestHeaterYear(encoded, null),
      water_heater_years: drafts,
    });
  };

  const setHeaterCount = (id, delta) => {
    const grouped = {};
    HEATER_SYSTEMS.forEach((row) => { grouped[row.id] = []; });
    heaterSlots.forEach((slot, index) => grouped[slot.id].push(heaterYears[index] || ''));
    if (!grouped[id]) grouped[id] = [];
    const count = Math.max(0, Math.min(8, grouped[id].length + delta));
    while (grouped[id].length < count) grouped[id].push('');
    grouped[id] = grouped[id].slice(0, count);
    writeHeaters(grouped);
  };

  const setHeaterYear = (index, text) => {
    const grouped = {};
    HEATER_SYSTEMS.forEach((row) => { grouped[row.id] = []; });
    const drafts = heaterYears.slice();
    drafts[index] = text.replace(/[^\d]/g, '').slice(0, 4);
    heaterSlots.forEach((slot, slotIndex) => grouped[slot.id].push(drafts[slotIndex] || ''));
    writeHeaters(grouped);
  };

  return (
    <div data-testid="home-facts-fields" style={{ display: 'grid', gap: '1.1rem' }}>
      <section>
        <h3 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.05rem' }}>Air conditioners</h3>
        <p style={{ margin: '0 0 0.65rem', color: colors.textSecondary }}>
          Add every unit. A home can have a central system and a mini split.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          {HVAC_SYSTEMS.map((system) => (
            <CountCard
              key={system.id}
              id={system.id}
              label={system.label}
              description={system.description}
              count={hvacSystems[system.id] || 0}
              readOnly={readOnly}
              brandColor={brandColor}
              onDelta={(delta) => setHvacCount(system.id, delta)}
            />
          ))}
        </div>
        {hvacSlots.length ? (
          <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
            {hvacSlots.map((slot, index) => (
              <label key={slot.id + '-' + index} style={{ color: colors.text, fontWeight: 650 }}>
                {slot.label + ' year'}
                <input
                  inputMode="numeric"
                  data-testid={`hvac-year-${slot.id}-${index}`}
                  value={hvacYears[index] || ''}
                  disabled={readOnly}
                  placeholder="2018"
                  onChange={(event) => setHvacYear(index, event.target.value)}
                  style={yearFieldStyle(colors)}
                />
              </label>
            ))}
          </div>
        ) : null}
      </section>

      {ducted > 0 ? <section>
        <h3 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.05rem' }}>AC filters</h3>
        <p style={{ margin: '0 0 0.65rem', color: colors.textSecondary }}>
          One reminder covers every unit. A 1 inch filter is monthly, a 4 inch filter is every 6 months, and a washable filter is every 3 months. Tap the selected type again to clear it.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          {FILTER_TYPES.map((id) => (
            <Tile
              key={id}
              id={`filter-${id}`}
              label={FILTER_LABELS[id]}
              selected={String(home.filter_type || '') === id}
              brandColor={brandColor}
              onClick={() => patch({ filter_type: String(home.filter_type || '') === id ? '' : id })}
            />
          ))}
        </div>
        <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
          {Array.from({ length: ducted }, (_, index) => (
            <label key={index} style={{ color: colors.text, fontWeight: 650 }}>
              {`AC ${index + 1} filter size`}
              <input
                data-testid={`filter-size-${index + 1}`}
                value={sizes[index] || ''}
                disabled={readOnly}
                placeholder="16x20x1"
                onChange={(event) => setSize(index, event.target.value)}
                style={{
                  display: 'block',
                  width: '100%',
                  boxSizing: 'border-box',
                  marginTop: 6,
                  minHeight: 48,
                  borderRadius: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  background: colors.surface,
                  color: colors.text,
                  padding: '0.6rem 0.75rem',
                  fontWeight: 500,
                }}
              />
            </label>
          ))}
        </div>
      </section> : null}

      <section>
        <h3 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.05rem' }}>Water heaters</h3>
        <p style={{ margin: '0 0 0.65rem', color: colors.textSecondary }}>
          Add every heater. A tank and a tankless unit can both be in the same home.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          {HEATER_SYSTEMS.filter((system) => system.kind !== 'solar' && system.id !== 'Heat Pump Water Heater' || (heaterSlots.some((slot) => slot.id === system.id))).map((system) => (
            <CountCard
              key={system.id}
              id={system.id}
              label={system.label}
              description={system.description}
              count={heaterSlots.filter((slot) => slot.id === system.id).length}
              readOnly={readOnly}
              brandColor={brandColor}
              onDelta={(delta) => setHeaterCount(system.id, delta)}
            />
          ))}
        </div>
        {heaterSlots.length ? (
          <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
            {heaterSlots.map((slot, index) => (
              <label key={slot.id + '-' + index} style={{ color: colors.text, fontWeight: 650 }}>
                {slot.label + ' year'}
                <input
                  inputMode="numeric"
                  data-testid={`heater-year-${index}`}
                  value={heaterYears[index] || ''}
                  disabled={readOnly}
                  placeholder="2016"
                  onChange={(event) => setHeaterYear(index, event.target.value)}
                  style={yearFieldStyle(colors)}
                />
              </label>
            ))}
          </div>
        ) : null}
      </section>

      <section data-home-part="water">
        <h3 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.05rem' }}>Water</h3>
        <p style={{ margin: '0 0 0.65rem', color: colors.textSecondary }}>
          Tap every source the house uses. City and a well can both be on.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          {HOUSE_WATER_SOURCES.map((source) => {
            const selected = houseWater.indexOf(source.id) !== -1;
            return (
              <button
                key={source.id}
                type="button"
                data-testid={`home-fact-water-${source.id}`}
                aria-pressed={selected}
                disabled={readOnly}
                onClick={() => patch({ water_source: toggleHouseWater(home.water_source, source.id) })}
                style={{
                  width: '100%',
                  minHeight: 48,
                  textAlign: 'left',
                  borderRadius: '0.85rem',
                  border: selected ? `2px solid ${brandColor}` : '1px solid #d5e4e4',
                  background: selected ? '#e7f7f7' : '#fff',
                  color: '#083838',
                  padding: '0.6rem 0.8rem',
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <span style={{ display: 'block', fontWeight: 700, color: '#083838' }}>{source.label}</span>
                <span style={{ display: 'block', color: '#3f6f6e', fontSize: '0.95rem' }}>{source.description}</span>
              </button>
            );
          })}
        </div>
        {hasWell(home) ? (
          <label style={{ display: 'block', marginTop: '0.65rem', color: colors.text, fontWeight: 650 }}>
            Well pump year
            <input
              inputMode="numeric"
              data-testid="water-well-year"
              value={yearInput(home.well_pump_install_year)}
              disabled={readOnly}
              placeholder="2018"
              onChange={(event) => patch({ well_pump_install_year: event.target.value.replace(/[^\d]/g, '').slice(0, 4) })}
              style={yearFieldStyle(colors)}
            />
          </label>
        ) : null}
      </section>

      <section data-home-part="water">
        <h3 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.05rem' }}>Irrigation water</h3>
        <p style={{ margin: '0 0 0.65rem', color: colors.textSecondary }}>
          Tap every source the sprinklers use. None clears the others. A well uses the same pump year as the rest of the home.
        </p>
        <div style={{ display: 'grid', gap: 10 }}>
          {IRRIGATION_SOURCES.map((source) => {
            const selected = irrigationSources(home).indexOf(source.id) !== -1;
            return (
              <button
                key={source.id}
                type="button"
                data-testid={`irrigation-${source.id}`}
                aria-pressed={selected}
                disabled={readOnly}
                onClick={() => patch({ irrigation_source: toggleIrrigation(home.irrigation_source, source.id) })}
                style={{
                  width: '100%',
                  minHeight: 48,
                  textAlign: 'left',
                  borderRadius: '0.85rem',
                  border: selected ? `2px solid ${brandColor}` : '1px solid #d5e4e4',
                  background: selected ? '#e7f7f7' : '#fff',
                  color: '#083838',
                  padding: '0.6rem 0.8rem',
                  cursor: readOnly ? 'default' : 'pointer',
                }}
              >
                <span style={{ display: 'block', fontWeight: 700, color: '#083838' }}>{source.label}</span>
                <span style={{ display: 'block', color: '#3f6f6e', fontSize: '0.95rem' }}>{source.description}</span>
              </button>
            );
          })}
        </div>
        {irrigationUsesWell(home) && !hasWell(home) ? (
          <label style={{ display: 'block', marginTop: '0.65rem', color: colors.text, fontWeight: 650 }}>
            Well pump year
            <input
              inputMode="numeric"
              data-testid="irrigation-well-year"
              value={yearInput(home.well_pump_install_year)}
              disabled={readOnly}
              placeholder="2018"
              onChange={(event) => patch({ well_pump_install_year: event.target.value.replace(/[^\d]/g, '').slice(0, 4) })}
              style={yearFieldStyle(colors)}
            />
          </label>
        ) : null}
      </section>

      <section>
        <h3 style={{ margin: '0 0 0.5rem', color: colors.text, fontSize: '1.05rem' }}>Sewer</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <Tile id="sewer-public" label="Public sewer" selected={sewer === SEWER_PUBLIC} brandColor={brandColor} onClick={() => patch({ sewer_type: [SEWER_PUBLIC], septic_install_year: null })} />
          <Tile id="sewer-septic" label="Septic" selected={sewer === SEWER_SEPTIC} brandColor={brandColor} onClick={() => patch({ sewer_type: [SEWER_SEPTIC] })} />
        </div>
        {sewer === SEWER_SEPTIC ? (
          <label style={{ display: 'block', marginTop: '0.65rem', color: colors.text, fontWeight: 650 }}>
            Septic year
            <input
              inputMode="numeric"
              value={yearInput(home.septic_install_year)}
              disabled={readOnly}
              placeholder="2004"
              onChange={(event) => patch({ septic_install_year: event.target.value.replace(/[^\d]/g, '').slice(0, 4) })}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6, minHeight: 48, borderRadius: '0.75rem', border: `1px solid ${colors.border}`, padding: '0.6rem 0.75rem', background: colors.surface, color: colors.text }}
            />
          </label>
        ) : null}
      </section>

      <section>
        <h3 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.05rem' }}>At this home</h3>
        <p style={{ margin: '0 0 0.65rem', color: colors.textSecondary }}>Tap what the home has. Tasks follow these.</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          {VISIBLE_FEATURES.map((feature) => (
            <Tile
              key={feature.id}
              id={`feature-${feature.id}`}
              label={feature.label}
              selected={featureSelectedVisible(home.features, feature.id)}
              brandColor={brandColor}
              onClick={() => patch({ features: toggleFeature(home.features, feature.id) })}
            />
          ))}
        </div>
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <label style={{ color: colors.text, fontWeight: 650 }}>
          Year built
          <input
            inputMode="numeric"
            value={yearInput(home.year_built)}
            disabled={readOnly}
            placeholder="2005"
            onChange={(event) => patch({ year_built: event.target.value.replace(/[^\d]/g, '').slice(0, 4) })}
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6, minHeight: 48, borderRadius: '0.75rem', border: `1px solid ${colors.border}`, padding: '0.6rem 0.75rem', background: colors.surface, color: colors.text }}
          />
        </label>
        <label style={{ color: colors.text, fontWeight: 650 }}>
          Square feet
          <input
            inputMode="numeric"
            value={yearInput(home.square_feet)}
            disabled={readOnly}
            placeholder="1800"
            onChange={(event) => patch({ square_feet: event.target.value.replace(/[^\d]/g, '').slice(0, 6) })}
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6, minHeight: 48, borderRadius: '0.75rem', border: `1px solid ${colors.border}`, padding: '0.6rem 0.75rem', background: colors.surface, color: colors.text }}
          />
        </label>
      </section>
      {equipment}
    </div>
  );
}
